import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { SparklesCore } from "./ui/sparkles";
import FlyingFlags from "./FlyingFlags";

// Animated count-up triggered when visible — animates from 0 to target with easing
const CountUp = ({ value, duration = 1400 }) => {
  const [display, setDisplay] = useState('0');
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) setStarted(true);
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const m = String(value).match(/^(\d+)(.*)$/);
    if (!m) { setDisplay(String(value)); return; }
    const target = parseInt(m[1], 10);
    const suffix = m[2] || '';
    let start, raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = Math.round(eased * target);
      setDisplay(current.toLocaleString('uk-UA') + suffix);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [value, duration, started]);

  return <span ref={ref}>{display}</span>;
};

const sidePhotos = [
  { src: "https://images.unsplash.com/photo-1544717305-2782549b5136?w=600", className: "left-[-4%] top-[8%] w-44 h-52 md:w-56 md:h-72", rotate: "-8deg", animClass: "float-card-1" },
  { src: "https://images.unsplash.com/photo-1583373834259-46cc92173cb7?w=600", className: "left-[-2%] bottom-[14%] w-44 h-56 md:w-56 md:h-72", rotate: "-12deg", animClass: "float-card-3" }
];

const Hero = ({ onOpenForm }) => {
  const { t } = useLang();
  const stats = t("stats");
  return (
    <section id="top" className="hero-light pt-28 md:pt-36 pb-20 md:pb-24 relative overflow-hidden">
      {/* Glow blobs */}
      <div className="glow-blob glow-pulse" style={{ top: '6%', right: '8%', width: 380, height: 380, background: 'rgba(201,169,110,0.28)' }} />
      <div className="glow-blob glow-pulse" style={{ bottom: '0%', right: '20%', width: 320, height: 320, background: 'rgba(232,213,171,0.25)', animationDelay: '-2s' }} />
      <div className="glow-blob glow-pulse" style={{ top: '40%', left: '30%', width: 460, height: 460, background: 'rgba(27,42,74,0.07)', animationDelay: '-1s' }} />

      {/* Sparkles overlay — subtle gold particles behind the headline */}
      <div className="pointer-events-none absolute inset-0 z-[1]" aria-hidden="true">
        <SparklesCore
          id="hero-sparkles"
          background="transparent"
          minSize={0.4}
          maxSize={1.2}
          particleDensity={70}
          particleColor="#C9A96E"
          speed={1.2}
          className="w-full h-full"
        />
      </div>

      {/* Flying country mini-flags */}
      <FlyingFlags />

      {/* Decorative side photos */}
      {sidePhotos.map((p, i) => (
        <div
          key={i}
          className={`absolute photo-card ${p.className} ${p.animClass} hidden md:block`}
          style={{ background: '#1B2A4A', transform: `rotate(${p.rotate})`, zIndex: 1 }}
        >
          <img src={p.src} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      ))}

      {/* Headline + CTAs */}
      <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center z-10">
        <div
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] md:text-xs font-bold tracking-[0.18em] uppercase mb-10 fade-up"
          style={{
            color: '#1B2A4A',
            background: 'linear-gradient(135deg, rgba(212,175,55,0.20), rgba(212,175,55,0.06))',
            border: '1px solid rgba(212,175,55,0.55)',
            boxShadow: '0 6px 18px -10px rgba(212,175,55,0.45)',
            animationDelay: '0.05s'
          }}
          data-testid="hero-badge"
        >
          <span style={{ color: '#C9A96E', fontSize: '15px', lineHeight: 1 }}>100%</span>
          <span>{t("hero.badge").replace(/^100\s*%\s*/, '')}</span>
        </div>

        <h1 className="font-display fade-up text-balance leading-[1.05]" style={{
          color: '#1B2A4A',
          fontFamily: "'Forum','Cormorant Garamond',serif",
          fontWeight: 400,
          fontSize: 'clamp(34px, 6.2vw, 88px)',
          letterSpacing: '-0.01em',
          animationDelay: '0.12s'
        }}>
          {t("hero.title3")}
        </h1>

        <p className="mt-8 text-base md:text-lg max-w-2xl mx-auto leading-relaxed fade-up" style={{ color: '#3a4f4f', animationDelay: '0.2s' }}>{t("hero.subtitle")}</p>

        <div className="mt-10 flex justify-center fade-up" style={{ animationDelay: '0.28s' }}>
          <button onClick={onOpenForm} className="btn-wavy">
            {t("cta.enrollGuarantee")} <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-12 flex items-center justify-center gap-4 fade-up" style={{ animationDelay: '0.36s' }}>
          <div className="flex -space-x-3">
            {[
              'https://images.unsplash.com/photo-1544717305-2782549b5136?w=80',
              'https://images.pexels.com/photos/37053780/pexels-photo-37053780.jpeg?w=80',
              'https://images.pexels.com/photos/36608621/pexels-photo-36608621.jpeg?w=80',
              'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80',
              'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80'
            ].map((u, i) => (
              <img key={i} src={u} alt="" className="w-10 h-10 rounded-full border-2 object-cover" style={{ borderColor: '#FAF8F5' }} />
            ))}
          </div>
          <div className="text-left text-sm">
            <div className="font-bold" style={{ color: '#1B2A4A' }}>1000+ {t("hero.rating")}</div>
          </div>
        </div>
      </div>

      {/* Stats — premium cream-tone band with brand gold accents */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 mt-12 relative z-10">
        <div
          className="relative rounded-2xl overflow-hidden px-5 py-6 md:py-7 fade-up"
          style={{
            background: 'linear-gradient(135deg, #F5EFE6 0%, #FAF8F5 50%, #F5EFE6 100%)',
            boxShadow: '0 10px 30px -18px rgba(27,42,74,0.18), inset 0 0 0 1px rgba(212,175,55,0.35)',
            animationDelay: '0.5s'
          }}
        >
          {/* Top gold accent */}
          <span aria-hidden="true" className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #C9A96E, transparent)' }} />
          {/* Soft gold corner glows */}
          <span aria-hidden="true" className="absolute -top-10 -left-10 w-28 h-28 rounded-full opacity-60" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)' }} />
          <span aria-hidden="true" className="absolute -bottom-10 -right-10 w-28 h-28 rounded-full opacity-60" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)' }} />

          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-y-5 gap-x-2 md:gap-x-1 items-center text-center">
            {Array.isArray(stats) && stats.map((s, i) => (
              <div key={i} data-testid={`hero-stat-${i}`} className="relative px-2 md:px-4 group">
                {i > 0 && (
                  <span aria-hidden="true" className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 h-12 w-px" style={{ background: 'linear-gradient(to bottom, transparent, rgba(212,175,55,0.5), transparent)' }} />
                )}
                <div
                  className="font-display leading-none tracking-tight transition-transform duration-500 group-hover:scale-105"
                  style={{
                    color: '#1B2A4A',
                    fontFamily: "'Forum','Cormorant Garamond',serif",
                    fontWeight: 400,
                    fontSize: 'clamp(34px, 4.4vw, 54px)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  <CountUp value={s.value} />
                </div>
                <div
                  className="mt-2 text-[10px] md:text-[11px] uppercase tracking-[0.18em] font-semibold leading-snug"
                  style={{ color: '#C9A96E' }}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
